import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { scenarioActions, selectScenario } from './scenarioSlice';

import {
  useGetScenarioQuery,
  useGetScenarioGroupQuery,
  useGetRiseTransportPathOnDateQuery,
} from '../services/edgeApi';
import { EditScenario } from './EditScenario';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { ScenarioDetail } from './ScenarioDetail';

import { SegmentConstraints } from './segmentConstraints/SegmentConstraints';
import { PointConstraints } from './pointConstraints/PointConstraints';
import { Markets } from './markets/Markets';
import { Trades } from './trades/Trades';
import { ScenarioContracts } from './contracts/ScenarioContracts';
import { ScenarioReceiptPoints } from './ScenarioReceiptPoints';
import { ComprehensiveView } from './ComprehensiveView';
// import { AllPathsTable } from './allPaths/AllPathsTable';
import { useAsyncOptimizeScenario } from '../hooks/useAsyncOptimizeScenario';
import { PathReport, Scenario } from '@/types';
import { OptimizationType, ScenarioComponentType } from '@/types/api/Scenario';
import { DefaultScenarioKeys } from '../types/api/Scenario';
import {
  comprehensiveView,
  riseChainView,
  getDataView,
} from '../services/dataViewService';
import { toIsoDate } from '../utils/dateUtils';
import { OptimizationChainsTable } from './chains/OptimizationChainsTable';
import { PathTable } from '../pathBuilder';
import { RootState } from '@/store';
import {
  ScenarioPathsStatus,
  useScenarioPaths,
} from '../hooks/useScenarioPaths';
import { PathReportPaths } from '../pathBuilder/PathReportPaths';
import { PathBuilderControlsRef } from '../pathBuilder/PathBuilderControls';
import { ScenarioPathStateDetail } from './ScenarioPathStateDetail';
import { LoadingIndicator } from '../helpers/LoadingIndicator';

export const RenderWithKey: React.FC<{
  children: (args: { key: string; id: string }) => JSX.Element;
}> = ({ children }) => {
  const params = useParams();
  const { id = DefaultScenarioKeys.NEW } = params;
  return children({ key: id, id });
};

export type ScenarioProps = {
  scenarioId: string;
  isEditing?: boolean;
  scenario?: Scenario;
};

export const ScenarioPage: React.FC<ScenarioProps> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scenarioId = props.scenarioId;

  const pathBuilderRef = useRef<PathBuilderControlsRef>(null);
  const [pathBuilderData, setPathBuilderData] = useState<PathReport[]>([]);

  const params = useParams();
  const { scenarioGroupId } = params;
  const groupUrlPrepend = scenarioGroupId
    ? `/scenarios/groups/${scenarioGroupId}`
    : '';

  // Don't fetch scenario if we're creating a new one
  const skip = !scenarioId || scenarioId === DefaultScenarioKeys.NEW;

  const stateScenario = useSelector(selectScenario);

  const [isEditing, setIsEditing] = useState(
    (props.isEditing || scenarioId === DefaultScenarioKeys.NEW) &&
      !stateScenario?.readonly,
  );
  const { data: riseChains } = useGetRiseTransportPathOnDateQuery(
    {
      gasDate: toIsoDate(stateScenario.flow_date),
    },
    { skip },
  );

  const {
    data: fetchedScenario,
    error,
    isLoading,
    isFetching,
  } = useGetScenarioQuery(
    { scenarioId: String(scenarioId), scenarioGroupId },
    { skip },
  );

  const {
    watch,
    optimize: asyncOptimize,
    saveAndOptimize: asyncSaveAndOptimize,
    state,
    message,
    optimizationError,
    optimizationProgress,
  } = useAsyncOptimizeScenario(scenarioId, {
    pingInterval: 10,
    skip:
      scenarioId.startsWith('default-') ||
      [
        DefaultScenarioKeys.NEXT_DAY,
        DefaultScenarioKeys.POSTMORTEM,
        DefaultScenarioKeys.NEW,
      ].includes(scenarioId as DefaultScenarioKeys),
  });

  const { build: buildScenarioPathsState, ...scenarioPathsState } =
    useScenarioPaths(stateScenario.id, isLoading || isFetching);

  const { data: scenarioGroup } = useGetScenarioGroupQuery(
    String(scenarioGroupId),
    {
      skip: !scenarioGroupId,
    },
  );

  const comprehensiveViewData = useMemo(() => {
    const providers = [
      {
        args: { scenario: stateScenario },
        providerFunction: comprehensiveView,
      },
      {
        args: { transports: riseChains ?? [] },
        providerFunction: riseChainView,
      },
    ];
    return getDataView({
      providers,
    });
  }, [stateScenario, riseChains]);

  useEffect(() => {
    return () => {
      dispatch(scenarioActions.resetState());
    };
  }, []);

  useEffect(() => {
    if (fetchedScenario) {
      dispatch(
        scenarioActions.setScenario({
          scenario: fetchedScenario,
        }),
      );
    }
  }, [fetchedScenario]);

  useEffect(() => {
    if (optimizationError) {
      toast.error(`Optimization Error: ${optimizationError}`);
    }
  }, [optimizationError]);

  // const pathBuilderData = useMemo(() => {
  //   console.log(
  //     'resolving path builder data with',
  //     pathBuilderRef.current?.getPaths(),
  //   );
  //   return pathBuilderRef.current?.getPaths() ?? [];
  // }, [pathBuilderRef.current?.getPaths()]);

  const enterEditingState = () => {
    dispatch(scenarioActions.beginEditing());
  };

  const rollbackEditingState = () => {
    dispatch(scenarioActions.rollback());
  };

  const commitEditingState = () => {
    dispatch(scenarioActions.commit());
  };

  const onEditSuccess = (result: Scenario) => {
    if (!result.id) {
      return;
    }
    dispatch(
      scenarioActions.setScenario({
        scenario: result,
      }),
    );
    setIsEditing(false);
    commitEditingState();
    // navigate to new scenario URL, but might want to do this conditionally
    // right now it does navigate to the new URL but it doesn't re-render this component, probably because there's no key set on the Scenario component in the routes.  I don't know if we really care because it seems to do what we want
    console.log(
      'navigating to',
      `${groupUrlPrepend}/scenarios/${result.id} from onEditSuccess`,
    );
    navigate(`${groupUrlPrepend}/scenarios/${result.id}`);
  };

  const onEditCancel = () => {
    setIsEditing(false);
    rollbackEditingState();
    // depending on where we are, we probably want to do different things when canceling
    // like on scenarios/new, we probably want to go back to where we were previously
    // but if we are editing an existing scenario, we probably want to stay at scenario detail
    if (scenarioId === 'new') {
      console.log(
        'navigating to',
        `/${groupUrlPrepend}scenarios from onEditCancel`,
      );
      navigate(`/${groupUrlPrepend}scenarios`);
    }
  };

  useEffect(() => {
    if (isEditing) {
      enterEditingState();
    }
  }, [isEditing]);

  if (isLoading) {
    return <>Loading Scenario</>;
  }

  if (error) {
    // Frequently seeing this on local runs from Path Service Not Available / Connection Refused
    toast.error('Problem loading scenario\nCheck path service availability');
    return null;
  }

  return (
    <>
      {isEditing ? (
        <EditScenario
          watch={watch}
          asyncSaveAndOptimize={asyncSaveAndOptimize}
          state={state}
          message={message}
          scenario={stateScenario}
          onSuccess={onEditSuccess}
          onCancel={onEditCancel}
        />
      ) : (
        stateScenario && (
          <>
            <ScenarioDetail
              scenarioId={scenarioId}
              watch={watch}
              asyncOptimize={asyncOptimize}
              state={state}
              message={message}
              scenarioGroup={scenarioGroup}
              optimizationError={optimizationError}
              optimizationProgress={optimizationProgress}
              onEditStart={() => setIsEditing(true)}
              onOptimize={onEditSuccess}
            />
            <ScenarioPathStateDetail
              ref={pathBuilderRef}
              scenario={stateScenario}
              state={scenarioPathsState}
              onBuild={buildScenarioPathsState}
              onGetPaths={setPathBuilderData}
            />
          </>
        )
      )}
      <ScenarioTabs
        pathsStatus={scenarioPathsState.status}
        key="scenario-tabs"
        isEditing={isEditing}
        isFinderPage={[ScenarioPathsStatus.SUCCESS].includes(
          scenarioPathsState.status,
        )}
        scenarioId={scenarioId}
        scenario={stateScenario}
        comprehensiveViewData={comprehensiveViewData}
        mode={stateScenario?.settings?.optimization_type}
        onBuildPathsState={buildScenarioPathsState}
        pathBuilderPaths={pathBuilderData}
      />
    </>
  );
};

type OptimizationTabsProps = {
  isEditing: boolean;
  isFinderPage: boolean;
  scenarioId: string;
  scenario: Scenario;
  comprehensiveViewData: any;
  mode: OptimizationType;
  pathsStatus: ScenarioPathsStatus;
  pathBuilderPaths: PathReport[];
  onBuildPathsState: () => void;
};

const ScenarioTabs: React.FC<OptimizationTabsProps> = ({
  isEditing,
  isFinderPage,
  scenarioId,
  scenario,
  comprehensiveViewData,
  // mode,
  pathsStatus,
  pathBuilderPaths,
  onBuildPathsState,
}) => {
  const [selectedComponentType, setSelectedComponentType] =
    useState<ScenarioComponentType>('segment_constraints');
  const stateReady = pathsStatus === ScenarioPathsStatus.SUCCESS;
  const kPathsData = useSelector((s: RootState) => s.pathFinder?.results ?? []);
  if (!scenario.id) {
    return null;
  }
  const startPaths = (
    <div className="d-flex flex-column justify-content-center align-items-center gap-3 h-100">
      {[ScenarioPathsStatus.IDLE, ScenarioPathsStatus.CANCELLED].includes(
        pathsStatus,
      ) ? (
        <>
          <span>
            {pathsStatus === ScenarioPathsStatus.CANCELLED
              ? 'The path service build for this scenario was cancelled'
              : 'The path service for this scenario is not built yet'}
          </span>
          <button className="btn btn-primary" onClick={onBuildPathsState}>
            Build Paths State
          </button>
        </>
      ) : (
        <>
          <span>Loading Paths State...</span>
          <LoadingIndicator text="" />
        </>
      )}
    </div>
  );

  const commonTabs = [
    <BelowMapTab
      key="scenario-inputs"
      tabName="Inputs"
      contextKey="scenario-inputs"
    >
      <div style={{ maxHeight: '100%' }} className="d-flex flex-column">
        <div
          className="input-group p-2"
          style={{ backgroundColor: 'var(--bs-body-bg)' }}
        >
          <select
            className="form-select"
            value={selectedComponentType}
            onChange={(evt) =>
              setSelectedComponentType(
                evt.target.value as ScenarioComponentType,
              )
            }
          >
            <option value="segment_constraints">Segment Constraints</option>
            <option value="point_constraints">Point Constraints</option>
            <option value="contracts">Contracts</option>
            <option value="markets">Market Prices</option>
            <option value="trades">Trades</option>
          </select>
        </div>
        <div
          style={{
            maxWidth: '100%',
            overflow: 'auto',
          }}
        >
          <div
            className={
              selectedComponentType !== 'segment_constraints' ? 'd-none' : ''
            }
          >
            <SegmentConstraints contextKey="segmentConstraints" />
          </div>
          <div
            className={
              selectedComponentType !== 'point_constraints' ? 'd-none' : ''
            }
          >
            <PointConstraints contextKey="pointConstraints" />
          </div>
          <div
            className={selectedComponentType !== 'contracts' ? 'd-none' : ''}
          >
            <ScenarioContracts />
          </div>
          <div className={selectedComponentType !== 'markets' ? 'd-none' : ''}>
            <Markets />
          </div>
          <div className={selectedComponentType !== 'trades' ? 'd-none' : ''}>
            <Trades />
          </div>
        </div>
      </div>
    </BelowMapTab>,
  ];

  const defaultTabs = [
    <BelowMapTab
      key="scenario-below-map-trades"
      tabName="Trades"
      contextKey="trades"
    >
      <Trades />
    </BelowMapTab>,
  ];

  const pathsToPointTabs = [
    ...defaultTabs,
    <BelowMapTab
      key="scenario-below-map-receipt-points"
      tabName="Receipt Points"
      contextKey="receiptPoints"
    >
      <ScenarioReceiptPoints isEditing={isEditing} />
    </BelowMapTab>,
  ];

  const finderTabs = [
    <BelowMapTab
      key={`pathreport-${scenarioId}-${scenario.flow_date}`}
      contextKey="path-report"
      tabName="Path Report"
      tabOrder={0}
      to={`/scenarios/${scenarioId}/path-report`}
      activeTab={location.pathname.includes(
        `/scenarios/${scenarioId}/path-report`,
      )}
      unmountOnBlur
    >
      {stateReady ? (
        <PathReportPaths
          state_ready={stateReady}
          scenario_id={scenarioId}
          gas_date={scenario.flow_date}
        />
      ) : (
        startPaths
      )}
    </BelowMapTab>,
    <BelowMapTab
      key={`pathbuilder-${scenarioId}-${scenario.flow_date}`}
      contextKey="path-builder"
      tabName="Path Builder"
      tabOrder={1}
      to={`/scenarios/${scenarioId}/path-builder`}
      activeTab={location.pathname.includes(
        `/scenarios/${scenarioId}/path-builder`,
      )}
    >
      {stateReady ? (
        <PathTable pathData={pathBuilderPaths} expandAll={true} />
      ) : (
        startPaths
      )}
    </BelowMapTab>,
    <BelowMapTab
      key={`pathfinder-${scenarioId}-${scenario.flow_date}`}
      contextKey="path-finder"
      tabName="Path Finder"
      tabOrder={2}
      to={`/scenarios/${scenarioId}/path-finder`}
      activeTab={location.pathname.includes(
        `/scenarios/${scenarioId}/path-finder`,
      )}
    >
      {stateReady ? (
        <PathTable
          pathData={kPathsData}
          sortable={true}
          initialSortKey={'profit'}
          initialSortDir={'desc'}
          showAvoidConstraints={true}
        />
      ) : (
        startPaths
      )}
    </BelowMapTab>,
  ];

  const optimizedScenarioTabs = [
    <BelowMapTab
      key="scenario-below-map-optimization-chains"
      tabName={'Chains'}
      tabOrder={0}
      contextKey="optimizationChains"
      activeTab
    >
      <OptimizationChainsTable />
    </BelowMapTab>,
    <BelowMapTab
      key="scenario-below-map-comprehensive-view"
      tabName="Comprehensive View"
      contextKey="comprehensiveView"
    >
      <ComprehensiveView scenario={scenario} data={comprehensiveViewData} />
    </BelowMapTab>,
  ];

  // @ts-ignore
  const displayTabs = {
    DEFAULT: defaultTabs,
    BEST_PATHS_TO_POINT: pathsToPointTabs,
  };

  return (
    <BelowMapSetter key="scenario-below-map-setter">
      {!isEditing &&
        !isFinderPage &&
        !scenario.readonly &&
        optimizedScenarioTabs}
      {!isEditing && scenarioId !== 'new' && finderTabs}
      {commonTabs}
      {
        // displayTabs[mode] ?? defaultTabs
      }
    </BelowMapSetter>
  );
};
